// Copyright (c) 2026 PGNS LLC

import { createRootRoute, createRoute, createRouter, Link } from '@tanstack/react-router';
import { StatusShell } from '@/components/status-shell';
import { IncidentDetailPage } from '@/routes/incident-detail';
import { IncidentsPage } from '@/routes/incidents';
import { OverviewPage } from '@/routes/overview';

/**
 * Errors and 404s render inside the shell rather than replacing it: a status
 * page that blanks out is itself an outage signal readers will misread.
 */
function ShellMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-8 text-center">
      <h1 className="text-xl font-semibold mb-2">{title}</h1>
      <p className="text-muted-foreground">{body}</p>
      <Link to="/" className="text-primary hover:underline mt-4 inline-block">
        Back to current status
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: StatusShell,
  errorComponent: () => (
    <ShellMessage
      title="Something went wrong"
      body="This page hit an unexpected error. The JSON API at /v1/summary is unaffected."
    />
  ),
  notFoundComponent: () => (
    <ShellMessage title="Page not found" body="That status page does not exist." />
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: OverviewPage,
});

const incidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/incidents',
  component: IncidentsPage,
});

const incidentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/incidents/$incidentId',
  component: function IncidentDetailRouteComponent() {
    const { incidentId } = incidentDetailRoute.useParams();
    return <IncidentDetailPage incidentId={incidentId} />;
  },
});

const routeTree = rootRoute.addChildren([indexRoute, incidentsRoute, incidentDetailRoute]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
